import type { IBaseComponentProps } from 'components/base/types'
import Button from 'components/button'
import EmptyState from 'components/empty-state'
import { prettyAssetType } from '@/utils/asset'
import { EAssetType } from 'findef'
import { defineComponent } from 'vue'
import PlusIcon from 'assets/img/svg/icons/plus.svg?url'
import SearchIcon from 'assets/img/svg/icons/search.svg?url'
import LinkIcon from 'assets/img/svg/icons/chain-link.svg?url'


// @ts-ignore
import IconBriefcase from 'components/icons/IconBriefcase.vue'
import { getAssetIconComponent } from '@/data/assetMapping'
import { popModal } from 'components/modal/store'
import { pushAddAssetModal } from '@/modules/asset/modals/add-asset'

export enum EEmptyTableStateType {
  NO_ASSETS = 'NO_ASSETS',
  NO_PROVIDERS = 'NO_PROVIDERS',
  FILTER_NOT_FOUND = 'FILTER_NOT_FOUND',
  QUERY_NOT_FOUND = 'QUERY_NOT_FOUND'
}

export type EmptyTableStateProps = IBaseComponentProps & {
  assetType: EAssetType
  type?: EEmptyTableStateType
  listed?: boolean
}

const EmptyTableState = defineComponent<EmptyTableStateProps>(
  (props) => {
    return () => {
      const AssetIcon = getAssetIconComponent(
        { type: props.assetType, listed: !! props.listed },
        { class: 'w-6 h-6 flex items-center justify-center content-center', size: '2rem', fill: 'currentColor' }
      ) //() => getAssetIconFromAssetType(props.assetType, 'w-6 h-6')

      const router = useRouter()

      const addAsset = () => {
        return (
          <Button
            icon={{
              src: PlusIcon,
              stroke: 'white',
              fill: 'currentColor',
              size: '0.8rem'
            }}
            onClick={() => router.push({ path: '/portfolio', query: { openAddAssetModal: 'true' } })}
          >
            Add Asset
          </Button>
        )
      }

      const connectAccounts = () => {
        return (
          <Button
            icon={{
              src: LinkIcon,
              stroke: 'white',
              size: '1.2rem'
            }}
            onClick={() => {
              pushAddAssetModal({
                onSave: async () => {
                  await popModal()
                  await router.push('/refresh')  // dummy route to force refresh
                  await router.push('/portfolio')
                },
				initialViewIndex: 2
              })
            }}
          >
            Connect Account
          </Button>
        )
      }

      if (props.type === EEmptyTableStateType.NO_ASSETS) {
        return (
          <div class="h-full flex flex-col flex-1">
            <EmptyState
              title="No Assets In Portfolio"
              subtitle="Add your first asset to start setting up your investment portfolio!"
              action={addAsset}
              iconSize="2rem"
              icon={() => <IconBriefcase />}
            />
          </div>
        )
      }

      if (props.type === EEmptyTableStateType.NO_PROVIDERS) {
        return (
          <div class="h-full flex flex-col flex-1">
            <EmptyState
              class="h-full"
              title="No Accounts Connected"
              subtitle="Connect your investment accounts at other providers to track your assets on Findex!"
              action={connectAccounts}
              iconSize="2rem"
              icon={{
                src: LinkIcon,
                stroke: 'currentColor',
                size: '2.15rem'
              }}
            />
          </div>
        )
      }

      if (props.type === EEmptyTableStateType.QUERY_NOT_FOUND) {
        return (
          <div class="h-full flex flex-col flex-1">
            <EmptyState
              title="No results"
              subtitle="Try searching for something else"
              iconSize="2rem"
              icon={{
                src: SearchIcon,
                stroke: 'currentColor',
                size: '2.15rem'
              }}
            />
          </div>
        )
      }

      if (props.assetType === EAssetType.UNDEFINED) {
        return (
          <EmptyState
            title={`No assets`}
            subtitle={`You've still not added any assets`}
            action={addAsset}
            iconSize="2rem"
            icon={() => {
              return <AssetIcon />
            }}
          />
        )
      }

      return (
        <EmptyState
          title={`No ${props.assetType && prettyAssetType(props.assetType, props.listed, [], true, true)}`}
          subtitle={`You've still not added any ${props.assetType && prettyAssetType(props.assetType, props.listed, undefined, true, false)} ${props.assetType !== EAssetType.EQUITY ? 'assets' : ''}`}
          iconSize="2rem"
          icon={() => {
            return <AssetIcon />
          }}
        />
      )
    }
  },
  { props: ['assetType', 'type', 'listed'] }
)

export default EmptyTableState
